import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button, Form, Table, Offcanvas } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import withPermission from '../../utils/withPermission';
import Swal from 'sweetalert2';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const DepartmentList = () => {
    const { user } = useAuth();
    const WithDeletePermission = withPermission(({ children }) => children, ['department-delete']);
    const WithEditPermission = withPermission(({ children }) => children, ['department-edit']);
    const WithCreatePermission = withPermission(({ children }) => children, ['department-create']);
    
    const [departments, setDepartments] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({ name: '', description: '', status: 'Active' });
    const [isEdit, setIsEdit] = useState(false);
    const [selectedDep, setSelectedDep] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        fetchDepartments();
    }, []);

    const fetchDepartments = async () => {
        setIsLoading(true);
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/departments`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                }
            });
            if (response.data.status === 'success') {
                setDepartments(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching departments:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAdd = () => {
        setFormData({ name: '', description: '', status: 'Active' });
        setIsEdit(false);
        setShowModal(true);
    };

    const handleEdit = (dep) => {
        setFormData({ name: dep.name, description: dep.description || '', status: dep.status });
        setIsEdit(true);
        setSelectedDep(dep);
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const userInfo = JSON.parse(localStorage.getItem('user-info'));
                const origin = localStorage.getItem('user-origin');
                const token = userInfo?.token;

                await axios.delete(`${backendUrl}/api/departments/${id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    }
                });
                setDepartments(departments.filter(d => d.id !== id));
                Swal.fire('Deleted!', 'Department has been deleted.', 'success');
            } catch (error) {
                Swal.fire('Error!', 'Failed to delete department.', 'error');
            }
        }
    };

    const handleSubmit = async () => {
        if (!formData.name) return;
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            if (isEdit) {
                const response = await axios.put(`${backendUrl}/api/departments/${selectedDep.id}`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    }
                });
                setDepartments(departments.map(d => d.id === selectedDep.id ? response.data.data : d));
            } else {
                const response = await axios.post(`${backendUrl}/api/departments`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    }
                });
                setDepartments([...departments, response.data.data]);
            }
            setShowModal(false);
            Swal.fire('Success!', `Department ${isEdit ? 'updated' : 'created'} successfully.`, 'success');
        } catch (error) {
            Swal.fire('Error!', 'Failed to save department.', 'error');
        }
    };

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                <h4 className="mb-sm-0 font-size-18">Department Management</h4>
                                <div className="page-title-right">
                                    <ol className="breadcrumb m-0">
                                        <li className="breadcrumb-item"><a href="javascript:void(0);">User</a></li>
                                        <li className="breadcrumb-item active">Department List</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between mb-3">
                                <div className="d-flex">
                                    <div className="search-box me-2 mb-2 d-inline-block">
                                        <div className="position-relative">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Search..."
                                                // value={searchQuery}
                                                // onChange={(e) => setSearchQuery(e.target.value)}
                                            />
                                            <i className="bx bx-search-alt search-icon" />
                                        </div>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <WithCreatePermission>
                                        <button
                                            type="button"
                                            className="btn rounded-circle border d-flex align-items-center justify-content-center round-create-button"
                                            onClick={handleAdd}
                                            data-bs-toggle="tooltip"
                                            data-bs-placement="top"
                                            title="Add Department"
                                        >
                                            <i className="mdi mdi-plus"></i>
                                        </button>
                                    </WithCreatePermission>
                                </div>
                            </div>
                            <div className="table-responsive">
                                <table className="table align-middle table-nowrap table-check table-list">
                                    <thead className="table-sales-gradient">
                                        <tr>
                                            <th style={{ width: '20px' }} className="align-middle">
                                                <div className="form-check font-size-16">
                                                    <input className="form-check-input" type="checkbox" id="checkAll" />
                                                    <label className="form-check-label" htmlFor="checkAll"></label>
                                                </div>
                                            </th>
                                            <th className="align-middle">#</th>
                                            <th className="align-middle">Department Name</th>
                                            <th className="align-middle">Description</th>
                                            <th className="align-middle">Status</th>
                                            <th className="align-middle">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isLoading ? (
                                            <tr><td colSpan="6" className="text-center">Loading...</td></tr>
                                        ) : departments.length > 0 ? (
                                            departments.map((dep, index) => (
                                                <tr key={dep.id}>
                                                    <td>
                                                        <div className="form-check font-size-16">
                                                            <input className="form-check-input" type="checkbox" id={`depCheck${index}`} />
                                                            <label className="form-check-label" htmlFor={`depCheck${index}`}></label>
                                                        </div>
                                                    </td>
                                                    <td>{index + 1}</td>
                                                    <td>{dep.name}</td>
                                                    <td>{dep.description}</td>
                                                    <td><span className={`badge bg-${dep.status === 'Active' ? 'success' : 'danger'}`}>{dep.status}</span></td>
                                                    <td>
                                                        <div className="dropdown">
                                                            <i
                                                                className="mdi mdi-dots-horizontal font-size-18"
                                                                data-bs-toggle="dropdown"
                                                                aria-expanded="false"
                                                                style={{ cursor: 'pointer' }}
                                                            ></i>
                                                            <ul className="dropdown-menu dropdown-menu-end font-size-14">
                                                                <li>
                                                                    <WithEditPermission>
                                                                        <button className="dropdown-item" onClick={() => handleEdit(dep)}>
                                                                            Edit
                                                                        </button>
                                                                    </WithEditPermission>
                                                                </li>
                                                                <li>
                                                                    <WithDeletePermission>
                                                                        <button className="dropdown-item" onClick={() => handleDelete(dep.id)}>
                                                                            Delete
                                                                        </button>
                                                                    </WithDeletePermission>
                                                                </li>
                                                                </ul>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="6" className="text-center">No departments found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                    </div>
                </div>
            </div>

            <Offcanvas show={showModal} onHide={() => setShowModal(false)} placement='end' style={{ width: '450px' }}>
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>{isEdit ? 'Edit Department' : 'Add Department'}</Offcanvas.Title>
                </Offcanvas.Header>
                <hr style={{ margin: 0 }} /><br />
                <Offcanvas.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Name</Form.Label>
                            <Form.Control 
                                type="text" 
                                value={formData.name} 
                                onChange={(e) => setFormData({...formData, name: e.target.value})} 
                                placeholder="Sales, Operations, etc."
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control 
                                as="textarea" 
                                rows={3} 
                                value={formData.description} 
                                onChange={(e) => setFormData({...formData, description: e.target.value})} 
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Select 
                                value={formData.status} 
                                onChange={(e) => setFormData({...formData, status: e.target.value})}
                            >
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                            </Form.Select>
                        </Form.Group>
                    </Form>
                </Offcanvas.Body>
                <div className="p-3 d-flex justify-content-end border-top">
                    <Button variant="secondary me-2" onClick={() => setShowModal(false)}>Close</Button>
                    <Button variant="primary" onClick={handleSubmit}>{isEdit ? 'Update' : 'Save'}</Button>
                </div>
            </Offcanvas>
        </div>
    );
};

export default withPermission(DepartmentList, ['department-list']);
